import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, CustomInput, Form, FormGroup, Input, Label } from 'reactstrap';
import {
  isNullOrWhitespace,
  titleCase,
  useStandardCollator,
} from 'js/utils/string';
import {
  compareCapabilityElements,
  displayThresholdToStored,
  parseStoredThreshold,
  storedThresholdToDisplay,
} from 'js/utils/capability-treemap-scores';
import styles from './CapabilitySetThresholdsForm.module.scss';
import { Icon } from 'js/components';
import { faPlus } from '@fortawesome/pro-regular-svg-icons';
import { faSpiderWeb } from '@fortawesome/pro-light-svg-icons';

export default function CapabilitySetThresholdsForm({
  capabilitySet,
  usedElementKeys = [],
  onSubmit,
}) {
  const collator = useStandardCollator();
  const storedThresholds = capabilitySet?.thresholds || {};
  const [extraKeys, setExtraKeys] = useState([]);
  const [overrides, setOverrides] = useState({});
  const [newKey, setNewKey] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const storedSignature = JSON.stringify(storedThresholds);

  useEffect(() => {
    setOverrides({});
  }, [storedSignature]);

  const usedByKey = useMemo(() => {
    const map = {};
    (usedElementKeys || []).forEach((item) => {
      if (item?.key) {
        map[item.key] = item;
      }
    });
    return map;
  }, [usedElementKeys]);

  const elements = useMemo(() => {
    const keys = new Set([
      ...Object.keys(storedThresholds),
      ...Object.keys(usedByKey),
      ...extraKeys,
    ]);

    return [...keys]
      .filter(Boolean)
      .map((key) => {
        const parsed = parseStoredThreshold(storedThresholds[key]);
        const base = {
          key,
          name: usedByKey[key]?.name || '',
          enabled: parsed.enabled,
          displayValue: storedThresholdToDisplay(parsed.value) || '0',
        };
        return { ...base, ...overrides[key] };
      })
      .sort((a, b) => compareCapabilityElements(a, b, collator));
  }, [storedThresholds, usedByKey, extraKeys, overrides, collator]);

  const existingKeys = useMemo(
    () => new Set(elements.map((row) => row.key)),
    [elements]
  );

  const patchRow = (key, patch) => {
    setOverrides((prev) => ({
      ...prev,
      [key]: { ...(prev[key] || {}), ...patch },
    }));
  };

  const handleAddKey = (event) => {
    event.preventDefault();
    const key = newKey.trim();
    if (!key) {
      return;
    }
    if (!existingKeys.has(key)) {
      setExtraKeys((prev) => [...prev, key]);
      patchRow(key, { enabled: true, displayValue: '0' });
    } else {
      patchRow(key, { enabled: true });
    }
    setNewKey('');
  };

  const handleSubmit = useCallback(
    async (event) => {
      event.preventDefault();
      const thresholds = {};
      elements.forEach((row) => {
        thresholds[row.key] = {
          value: displayThresholdToStored(row.displayValue),
          enabled: !!row.enabled,
        };
      });
      setIsSubmitting(true);
      try {
        await onSubmit({ thresholds });
      } finally {
        setIsSubmitting(false);
      }
    },
    [onSubmit, elements]
  );

  const addMeasurementFields = (
    <FormGroup className={styles.add}>
      <Label for="capability-set-new-element-key">
        Add a measurement by key
      </Label>
      <div className={styles['add-row']}>
        <Input
          id="capability-set-new-element-key"
          value={newKey}
          onChange={(event) => setNewKey(event.target.value)}
          placeholder="e.g. health"
        />
        <Button type="button" size="sm" color="primary" onClick={handleAddKey}>
          <Icon icon={faPlus} />
        </Button>
      </div>
    </FormGroup>
  );

  return (
    <Form onSubmit={handleSubmit}>
      {elements.length === 0 ? (
        <div className="media text-muted mt-4 mb-4">
          <Icon icon={faSpiderWeb} size="4x" className="mr-3" fixedWidth />
          <div className="media-body">
            <p>You haven't measured any of these capabilities yet..</p>
            <p>
              Add a measurement key now, or wait until an assessment uses this
              capability set and the measurements will be listed here.
            </p>
            {addMeasurementFields}
          </div>
        </div>
      ) : (
        <>
          <p className={styles.help}>
            Set an optional score target (0–100) for capability measures. These
            are shown on some charts as a target line.
          </p>
          <div className={styles.elements}>
            <div className={styles['elements-header']}>
              <div>
                <span className={styles.key}>Measurement</span>
              </div>
              <div>
                <span className={styles.key}>Target</span>
              </div>
            </div>
            {elements.map((element) => (
              <div key={element.key} className={styles.element}>
                <div>
                  <CustomInput
                    type="checkbox"
                    id={`threshold-enabled-${element.key}`}
                    checked={element.enabled}
                    onChange={(event) =>
                      patchRow(element.key, { enabled: event.target.checked })
                    }
                    label={
                      <span className={styles.label}>
                        <span className={styles.key}>{element.key}</span>
                        <span className={styles.name}>
                          {!isNullOrWhitespace(element.name)
                            ? element.name
                            : titleCase(element.key)}
                        </span>
                      </span>
                    }
                  />
                </div>
                <div>
                  <Input
                    type="text"
                    inputMode="decimal"
                    className={styles.target}
                    disabled={!element.enabled}
                    value={element.displayValue}
                    onChange={(event) =>
                      patchRow(element.key, {
                        displayValue: event.target.value,
                      })
                    }
                    onBlur={(event) =>
                      patchRow(element.key, {
                        displayValue: storedThresholdToDisplay(
                          displayThresholdToStored(event.target.value)
                        ),
                      })
                    }
                    aria-label={`${element.key} threshold`}
                  />
                </div>
              </div>
            ))}
          </div>
          {addMeasurementFields}
          <Button color="primary" type="submit" disabled={isSubmitting}>
            Update thresholds
          </Button>
        </>
      )}
    </Form>
  );
}
